import type { Object3D } from "three";
import type { Engine } from "../../../../engine/Engine";
import { raycast } from "../../../../physics/PhysicsUtil";
import type { PlanarGraphController } from "../../../../shape/segment/PlanarGraphController";
import type { XzPoint } from "../../../../shape/segment/types";
import { pointerXzOnFloor, pointerXzOnHostWall } from "./planePointer";

export type OpeningDragPointer = {
  point: XzPoint;
  hitHostId?: string;
};

export function segmentIdFromObject(object: Object3D): string | undefined {
  let current: Object3D | null = object;
  while (current) {
    const id = current.userData?.segmentId;
    if (typeof id === "string" && id.length > 0) return id;
    current = current.parent;
  }
  return undefined;
}

/**
 * 3D opening drag pointer: mesh hit on a host wall, else the live host plane.
 * `liveHostId` is the wall the opening is on this drag (may differ from the graph).
 */
export function resolveOpeningDragPointer(
  segments: PlanarGraphController,
  engine: Engine,
  openingId: string,
  event: PointerEvent,
  liveHostId?: string,
): OpeningDragPointer {
  const graph = segments.planarGraph;
  const opening = graph.segments.find(
    (candidate) => candidate.spec.id === openingId,
  )?.spec;
  const graphHostId = opening?.placement?.hostSegmentId;
  if (!graphHostId || !opening?.placement) {
    return { point: pointerXzOnFloor(event, engine) };
  }
  const hostId = liveHostId ?? graphHostId;
  const wallHit = hostWallMeshHit(segments, engine, event, openingId, hostId);
  if (wallHit) return wallHit;
  const host = graph.segments.find((candidate) => candidate.spec.id === hostId);
  if (!host) return { point: pointerXzOnFloor(event, engine) };
  const t =
    hostId === graphHostId
      ? (opening.placement.startT + opening.placement.endT) * 0.5
      : 0.5;
  return { point: pointerXzOnHostWall(event, engine, host.spec.path, t) };
}

function hostWallMeshHit(
  segments: PlanarGraphController,
  engine: Engine,
  event: PointerEvent,
  openingId: string,
  liveHostId: string,
): OpeningDragPointer | null {
  const camera = engine.activeCamera?.instance;
  const canvas = engine.canvas;
  const targets = segments.pickTargets3D;
  if (!camera || !canvas || !targets.length) return null;
  const hits = raycast(event, targets as Object3D[], camera, canvas);
  if (!hits?.length) return null;
  const graph = segments.planarGraph;
  const hostHits: OpeningDragPointer[] = [];
  for (const hit of hits) {
    const hitId = segmentIdFromObject(hit.object);
    if (!hitId || hitId === openingId) continue;
    const entry = graph.segments.find((candidate) => candidate.spec.id === hitId);
    if (!entry || entry.spec.placement) continue;
    hostHits.push({
      point: { x: hit.point.x, z: hit.point.z },
      hitHostId: hitId,
    });
  }
  return hostHits.find((hit) => hit.hitHostId === liveHostId) ?? hostHits[0] ?? null;
}
